/**
 * Ticket ME1: Öffentliche Übersicht der Mentorings.
 *
 * Aufbau wie die Kursübersicht (Ticket SK5) und über denselben Baustein
 * `OfferList` – ein zweites Erscheinungsbild für dieselbe Sache wäre der Anfang
 * vom Auseinanderlaufen. Unterschied zu den Kursen: Mentorings werden angefragt,
 * nicht gekauft. Deshalb steht in den Zusatzangaben ein Klartextpreis und die
 * Karte führt nur auf die Detailseite.
 *
 * Solange der Funktionsschalter `mentorings_visible` zu ist, liefert die
 * Leseschicht eine leere Liste an Besucher; nur angemeldete interne Rollen
 * sehen die Seiten.
 */

import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { OfferList, type OfferListItem } from "@/components/offers/OfferList";
import { AudiencePills } from "@/components/mentorings/AudiencePills";
import { PriceWithStrike } from "@/components/mentorings/PriceWithStrike";



import { listPublicMentorings } from "@/lib/mentorings.functions";
import { MENTORING_PREFIX, type MentoringCard } from "@/lib/mentorings";
import {
  jsonLdScript,
  mentoringListJsonLd,
  siteUrl,
} from "@/lib/structured-data";

const TITLE = "Mentorings für politische Kommunikation | creategy";
const DESCRIPTION =
  "Persönliche Begleitung von creategy: Mentorings für Wahlkampf, Amt und Verbandsarbeit – mit festem Ablauf, klarem Umfang und einer Ansprechperson.";

const EMPTY_TEXT =
  "Derzeit ist kein Mentoring ausgeschrieben. Sprechen Sie uns gern direkt an – wir sagen Ihnen, was zu Ihrem Vorhaben passt.";

export const Route = createFileRoute("/wissen/mentorings/")({
  loader: async () => {
    const mentorings = await listPublicMentorings();
    return { mentorings };
  },
  head: ({ loaderData }) => ({
    links: [{ rel: "canonical", href: siteUrl(MENTORING_PREFIX) }],
    scripts: loaderData ? jsonLdScript(mentoringListJsonLd(loaderData.mentorings)) : [],
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  errorComponent: MentoringsError,
  component: MentoringsOverviewPage,
});

function MentoringsError() {
  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <h1 className="heading-h1">Mentorings</h1>
          <p className="mt-4 max-w-prose font-body text-base text-muted-foreground">
            Die Übersicht ist im Moment nicht abrufbar. Bitte laden Sie die Seite noch einmal –
            oder sprechen Sie uns direkt an.
          </p>
        </div>
      </section>
    </Layout>
  );
}

/**
 * Zusatzangaben einer Karte: Zielgruppen als Pillen, Umfang, Preis.
 *
 * Ticket ME2: kein Gesicht mehr auf der Karte – die Betreuung steht erst auf der
 * Detailseite. Der Streichpreis erscheint nur, wenn er gepflegt ist.
 */
export function mentoringOfferItems(mentorings: MentoringCard[]): OfferListItem[] {
  return mentorings.map((mentoring) => ({
    id: mentoring.slug,
    title: mentoring.title,
    /* Ticket ME3: kein Untertitel mehr – die Karte trägt die Kurzbeschreibung. */
    shortDescription: mentoring.shortDescription || undefined,
    imageUrl: mentoring.previewImageUrl,
    to: `${MENTORING_PREFIX}/$slug`,
    params: { slug: mentoring.slug },
    ctaLabel: "Zum Mentoring",
    category: <AudiencePills audiences={mentoring.audiences} className="mt-3" />,
    meta: (
      /* Die Dauer steht als eigene Zeile über dem Block mit Preis und Knopf
         (w-full zwingt den Umbruch im umgebenden Flex-Block von OfferList). */
      <>
        {mentoring.scopeLabel ? (
          <dl className="w-full font-body text-sm text-muted-foreground">
            <dt className="font-semibold text-foreground">Dauer</dt>
            <dd>{mentoring.scopeLabel}</dd>
          </dl>
        ) : null}
        <PriceWithStrike
          priceLabel={mentoring.priceLabel}
          strikeLabel={mentoring.priceStrikethroughLabel}
          emphasis
          hideNote
          as="h2"
        />
      </>
    ),
  }));
}


function MentoringsOverviewPage() {
  const { mentorings } = Route.useLoaderData();

  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <header>
            <h1 className="heading-h1">Mentorings</h1>
            <p className="mt-5 max-w-prose font-body text-base leading-relaxed text-muted-foreground">
              Persönliche Begleitung statt Kurs: Sie arbeiten an Ihrem eigenen Vorhaben, mit
              festen Terminen und einer Ansprechperson aus unserem Haus.
            </p>
          </header>

          <div className="mt-12 lg:mt-16">
            <OfferList
              headingLevel="h2"
              emptyText={EMPTY_TEXT}
              ctaPlacement="right"
              imageAspect="square"
              titleClassName="text-2xl md:text-3xl"
              items={mentoringOfferItems(mentorings)}
            />
          </div>

        </div>

      </section>
    </Layout>
  );
}
